import { computed, effect, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import type { MultiPolygon as GeoJsonMultiPolygon } from 'geojson';
import { omit as radashOmit, parallel as radashParallel } from 'radash';
import { firstValueFrom, Subject } from 'rxjs';
// @ts-expect-error No type declaration type exists
import { parse as wktParse } from 'wkt';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { MapBaseSource } from '@connect-services/client/components/map/map.component';
import { FetchEntitiesProgressModalComponent } from '@connect-services/client/components/modals/field-intervention/fetch-entities-progress-modal/fetch-entities-progress-modal.component';
import { FieldInterventionCreationEditionForm } from '@connect-services/client/models/create-edit-field-intervention-modal.model';
import { AbstractFieldInterventionListService } from '@connect-services/client/services/abstract-field-intervention-list.service';
import { AssertService } from '@connect-services/client/services/assert.service';
import {
    FIELD_INTERVENTION_AREA_MASK_ID,
    MapMaskService,
    ZONE_DELIMITER_MASK_ID,
} from '@connect-services/client/services/map/mask/map-mask.service';
import { MapLayerService } from '@connect-services/client/services/map-layer.service';
import { MapGeoJSONFeatureWithLayer } from '@connect-services/client/services/map-popup.service';
import { SourcesService } from '@connect-services/client/services/sources.service';
import { RecordDataKey } from '@connect-services/src/lib/components/table/table.component';
import {
    CreateFieldInterventionDto,
    CreateFieldInterventionEntityDto,
    DataEntity,
    FieldIntervention,
    FieldInterventionEntitiesService,
    FieldInterventionEntity,
    FieldInterventionEntityControllerGet200Response,
    FieldInterventionsService,
    Layer,
    LayersService,
} from '@connect-services/src/sdk/api';
import {
    defaultPaginationParameters,
    OrderEnum,
    paginationMaxTake,
    PaginationParameters,
} from '@shared/client/models/pagination.model';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

export interface Entity extends DataEntity, RecordDataKey {}

@Injectable({
    providedIn: 'root',
})
export class CreateFieldInterventionService extends AbstractFieldInterventionListService<Entity, Layer> {
    public alreadySelectedFieldInterventionEntities: FieldInterventionEntity[] = [];
    public readonly errorMessage = computed(() =>
        this.isError()
            ? 'notSameAttributes'
            : this.isFilterOrSearchActive()
              ? 'noLayerDataEntitiesWithFilter'
              : 'noLayerDataEntities',
    );
    public readonly fetchedEntitiesCount = signal<number>(0);
    public readonly fieldInterventionAreaSelectionConfirmed = new Subject<void>();
    public readonly fieldInterventionId = computed(() => this.fieldInterventionBloc.value()?.id ?? '');
    public readonly fieldInterventionName = computed(() => this.fieldInterventionBloc.value()?.name ?? '');
    public readonly forceReset = signal(false);
    public readonly groupedLayer = signal<string[][]>([[]]);
    public readonly identifyingProperty = signal('');
    public readonly isCreateFieldIntervention = signal(false);
    public readonly isError = signal(false);
    public readonly isSelectionEntitiesValidated: WritableSignal<boolean> = signal<boolean>(false);
    public readonly order = signal<OrderEnum | undefined>(undefined);
    public readonly orderBy = signal('');
    public readonly selectedLayerNumber = computed(() =>
        this.groupedLayer().reduce((accumulator, groupedLayerIds) => {
            const selectedEntities = groupedLayerIds
                .map((layerId) => this.selectedEntities()[layerId])
                .flatMap((entity) => entity);

            if (
                selectedEntities.length ||
                this.fullSelectedLayers().some((layerId) => groupedLayerIds.includes(layerId))
            ) {
                return accumulator + 1;
            }

            return accumulator;
        }, 0),
    );
    public readonly unselectedEntitiesNumber = computed(() => {
        const currentSelectedIds = new Set(
            Object.values(this.selectedEntities()).flatMap((entities) => entities.map((e) => e.uuid)),
        );

        return this.alreadySelectedFieldInterventionEntities.filter(
            (initialEntity) => initialEntity.refSid && !currentSelectedIds.has(initialEntity.refSid),
        ).length;
    });
    public readonly updatedEntities = signal<number>(0);

    private readonly layersEntitiesSelectedByDelimiter = signal<Map<string, Entity[]>>(new Map());

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private readonly fieldInterventionEntitiesService = inject(FieldInterventionEntitiesService);
    private readonly fieldInterventionService = inject(FieldInterventionsService);
    private readonly layersService = inject(LayersService);
    private readonly mapLayerService = inject(MapLayerService);
    private readonly mapMaskService = inject(MapMaskService);
    private readonly matDialog = inject(MatDialog);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly snackBarService = inject(SnackBarService);
    private readonly sourcesService = inject(SourcesService);

    public constructor() {
        super();
        effect(() => {
            const isActive = this.isZoneDelimiterFilterActive();
            const wktString = this.zoneDelimiter();

            if (isActive && wktString) {
                try {
                    const geoJson = wktParse(wktString);

                    if (geoJson && (geoJson.type === 'Polygon' || geoJson.type === 'MultiPolygon')) {
                        const areaMultiPolygon: GeoJsonMultiPolygon =
                            geoJson.type === 'Polygon'
                                ? { coordinates: [geoJson.coordinates], type: 'MultiPolygon' }
                                : { coordinates: geoJson.coordinates, type: 'MultiPolygon' };

                        this.mapMaskService.applyMask(
                            {
                                area: areaMultiPolygon,
                                id: ZONE_DELIMITER_MASK_ID,
                                showMask: false,
                            },
                            {
                                hideOuterEntities: true,
                            },
                        );
                    }
                } catch {}
            } else {
                this.mapMaskService.clearMasks(['zone-delimiter-mask']);
            }
        });
    }

    public async changeActiveLayer(layers: Layer[]): Promise<void> {
        try {
            const currentSources: MapBaseSource<unknown>[] = await this.mapLayerService.disableLayersZoom(layers);

            this.currentSources.set(currentSources);
        } catch {
            this.snackBarService.openErrorSnackBar('errorWhileFetchingSelectedLayerData');
        } finally {
            this.initDataForLayer(layers);
        }
    }

    public clearFieldInterventionSelectionFilter(): void {
        this.isZoneDelimiterFilterActive.set(false);
        this.zoneDelimiter.set(undefined);

        if (!this.activeLayers().length) {
            return;
        }

        const selectedEntities = this.activeLayers().flatMap((layer: Layer) => this.selectedEntities()[layer.id] ?? []);

        if (selectedEntities.length) {
            this.updateVisibleFieldInterventionAreaEntities(selectedEntities, false);
            this.manageHighlight();

            return;
        }

        this.manageHighlight();
    }

    public confirmFieldInterventionAreaSelection(): void {
        this.fieldInterventionAreaSelectionConfirmed.next();
    }

    public createFieldIntervention(
        identifyFieldInterventionCreationForm: FieldInterventionCreationEditionForm,
    ): Promise<FieldIntervention> {
        const fieldInterventionArea = identifyFieldInterventionCreationForm.controls.fieldInterventionArea.value;

        if (!fieldInterventionArea) {
            throw new Error('Field intervention area is required.');
        }

        const fieldInterventionRequestParams: CreateFieldInterventionDto = {
            description: identifyFieldInterventionCreationForm.controls.description.value || undefined,
            fieldInterventionArea,
            name: identifyFieldInterventionCreationForm.controls.name.value,
            project: this.projectBloc.value()!.id,
            scheduledEndDate: identifyFieldInterventionCreationForm.controls.scheduledEndDate.value?.toISOString(),
        };

        return firstValueFrom(
            this.fieldInterventionService.fieldInterventionControllerPost({
                createFieldInterventionDto: fieldInterventionRequestParams,
            }),
        );
    }

    public override async fetchAllEntities(): Promise<Entity[]> {
        this.fetchedEntitiesCount.set(0);

        if (this.activeLayers().some((layer: Layer) => !layer.deposit)) {
            return [];
        }

        const currentFetchingLayerIds: string[] = this.activeLayers()
            .map((layer): string => layer.id)
            .toSorted();

        let hasNextPage = false;
        const paginationParameters: PaginationParameters = {
            ...defaultPaginationParameters,
            take: paginationMaxTake,
        };

        const dataEntities: Entity[] = [];

        this.isLoading.set(true);

        const dialogRef = this.openFetchEntitiesProgressModal();

        const zoneDelimiter = this.getCurrentZoneDelimiterIfFilterActive();

        do {
            const paginatedEntities = await firstValueFrom(
                this.layersService.layerControllerSearchEntitiesByLayers({
                    filterLayerEntityDto: {
                        hasGeom: true,
                        intersectsFieldInterventionArea: this.fieldInterventionId(),
                        layerIds: currentFetchingLayerIds,
                        onlyBookmarked: true,
                        zonesDelimiter: zoneDelimiter ? [zoneDelimiter] : undefined,
                    },
                    ...paginationParameters,
                    // `Order` and `OrderBy` are very important, as without them, certain entities are ignored.
                    order: this.order(),
                    orderBy: 'uuid',
                    properties: [this.identifyingProperty()],
                    value: this.searchValue(),
                }),
            );

            dataEntities.push(...paginatedEntities.items);
            hasNextPage = paginatedEntities.hasNextPage;
            paginationParameters.page += 1;

            this.fetchedEntitiesCount.update((count: number): number => count + paginatedEntities.items.length);
        } while (
            hasNextPage &&
            zoneDelimiter === this.getCurrentZoneDelimiterIfFilterActive() &&
            currentFetchingLayerIds.join('') ===
                this.activeLayers()
                    .map((layer): string => layer.id)
                    .toSorted()
                    .join('')
        );

        dialogRef.close();
        this.isLoading.set(false);

        return dataEntities;
    }

    public override async fetchEntities(): Promise<void> {
        if (this.activeLayers().some((layer: Layer) => !layer.deposit)) {
            return;
        }

        this.isLoading.set(true);

        const zoneDelimiter = this.isZoneDelimiterFilterActive() ? this.zoneDelimiter() : undefined;

        try {
            const paginatedEntities = await firstValueFrom(
                this.layersService.layerControllerSearchEntitiesByLayers({
                    filterLayerEntityDto: {
                        hasGeom: true,
                        intersectsFieldInterventionArea: this.fieldInterventionId(),
                        layerIds: this.activeLayers().map((layer: Layer) => layer.id),
                        onlyBookmarked: true,
                        zonesDelimiter: zoneDelimiter ? [zoneDelimiter] : undefined,
                    },
                    ...this.paginationParameters(),
                    order: this.order(),
                    orderBy: this.orderBy(),
                    properties: [this.identifyingProperty()],
                    value: this.searchValue(),
                }),
            );

            this.paginationParameters.set({
                ...this.paginationParameters(),
                take: paginatedEntities.take,
                totalItems: paginatedEntities.totalItems,
            });

            this.entities.set(paginatedEntities.items);

            this.setEntityType();

            if (!this.searchValue() && !this.isZoneDelimiterFilterActive()) {
                for (const layer of this.activeLayers()) {
                    this.setSignalValue<number>(this.totalItemsByLayer, layer.id, paginatedEntities.totalItems);
                }
            }

            this.manageHighlight();
        } catch {
            this.snackBarService.openErrorSnackBar('notSameAttributes');
            this.isError.set(true);
        } finally {
            this.isLoading.set(false);
        }
    }

    public override async handleSelectAllFilter(): Promise<void> {
        const totalItems = this.paginationParameters().totalItems ?? 0;
        const loadedEntities = this.entities();

        if (totalItems <= loadedEntities.length) {
            await super.handleSelectAllFilter(loadedEntities);

            return;
        }

        const filterKey = `${this.zoneDelimiter() ?? ''}___${this.searchValue() ?? ''}`;
        const cachedEntities: Entity[] | undefined = this.layersEntitiesSelectedByDelimiter().get(filterKey);

        if (cachedEntities) {
            await super.handleSelectAllFilter(cachedEntities);

            return;
        }

        const entities: Entity[] = await this.fetchAllEntities();
        await super.handleSelectAllFilter(entities);

        this.layersEntitiesSelectedByDelimiter.update(() => {
            const updatedCache = new Map();
            updatedCache.set(filterKey, entities);

            return updatedCache;
        });
    }

    public initDataForLayer(layers: Layer[]): void {
        this.forceReset.set(true);
        this.activeLayers.set(layers);
        this.paginationParameters.set(defaultPaginationParameters);
        //Both layer have the same businessIdentifier
        this.identifyingProperty.set(layers[0].businessIdentifier ?? 'uuid');
        this.searchValue.set('');
    }

    public async initFieldInterventionEntities(fieldInterventionId: string): Promise<void> {
        let hasNextPage = false;
        const paginationParameters: PaginationParameters = { ...defaultPaginationParameters };

        do {
            const paginatedEntities: FieldInterventionEntityControllerGet200Response = await firstValueFrom(
                this.fieldInterventionEntitiesService.fieldInterventionEntityControllerGet({
                    ...paginationParameters,
                    fieldIntervention: fieldInterventionId,
                    take: paginationMaxTake,
                }),
            );

            const filteredEntities = paginatedEntities.items.filter(
                (entity: FieldInterventionEntity) => entity.refSid !== null,
            );

            hasNextPage = paginatedEntities.hasNextPage;
            this.alreadySelectedFieldInterventionEntities.push(...filteredEntities);
            paginationParameters.page += 1;
        } while (hasNextPage);

        const groupedEntities = this.alreadySelectedFieldInterventionEntities.reduce(
            (
                acc: {
                    [key: string]: Entity[];
                },
                entity: FieldInterventionEntity,
            ): { [key: string]: Entity[] } => {
                if (!acc[entity.layer]) {
                    acc[entity.layer] = [];
                }

                acc[entity.layer].push({
                    ...entity.dataAttributes,
                    businessIdentifier: entity.dataAttributes['businessIdentifier'],
                    deposit_id: entity.dataAttributes['deposit_id'],
                    geom: entity.geom,
                    layer_id: entity.layer,
                    uuid: entity.refSid!,
                });

                return acc;
            },
            {},
        );

        this.selectedEntities.set(groupedEntities);
    }

    public override async onPageChange(page: number): Promise<void> {
        this.paginationParameters.set({
            ...this.paginationParameters(),
            page,
        });

        await this.fetchEntities();
    }

    public async refreshTable(): Promise<void> {
        await this.fetchEntities();
    }

    public resetData(): void {
        this.isCreateFieldIntervention.set(false);
        this.activeLayers.set([]);
        this.fullSelectedLayers.set([]);
        this.paginationParameters.set(defaultPaginationParameters);
        this.searchValue.set('');
        this.selectedEntities.set({});
        this.totalItemsByLayer.set({});
        this.unselectedEntities.set({});
        this.currentSources.set([]);
        this.zoneDelimiter.set(undefined);
        this.isZoneDelimiterFilterActive.set(false);
        this.sourcesService.sources.set([]);
        this.alreadySelectedFieldInterventionEntities = [];
    }

    public showFieldInterventionAreaMask(): void {
        const fieldInterventionArea = this.fieldInterventionBloc.value()?.fieldInterventionArea;
        if (!fieldInterventionArea) {
            return;
        }

        this.mapMaskService.applyMask(
            {
                area: fieldInterventionArea,
                id: FIELD_INTERVENTION_AREA_MASK_ID,
                showMask: true,
            },
            {
                hideOuterEntities: true,
            },
        );
    }

    public async upsertFieldInterventionEntities(): Promise<void> {
        this.updatedEntities.set(0);

        const fieldInterventionZoneDelimiter = this.getFieldInterventionAreaZoneDelimiter();

        for (const layerId of Object.keys(this.selectedEntities())) {
            if (this.fullSelectedLayers().includes(layerId)) {
                const excludedIds = this.unselectedEntities()[layerId].map((dataEntity) => dataEntity.uuid);
                await firstValueFrom(
                    this.fieldInterventionEntitiesService.fieldInterventionEntityControllerPostAllEntitiesFromLayer({
                        createFieldInterventionEntitiesDto: {
                            excludedIds: excludedIds,
                            fieldIntervention: this.fieldInterventionId(),
                            layer: layerId,
                            zonesDelimiter: fieldInterventionZoneDelimiter
                                ? [fieldInterventionZoneDelimiter]
                                : undefined,
                        },
                    }),
                );

                this.updatedEntities.update((count: number): number => count + this.selectedEntities()[layerId].length);
                continue;
            }

            await this.updateFieldInterventionEntitiesForLayer(layerId);
        }

        this.mapHighlightEntityService.resetHighlight();
    }

    protected override buildEntityFromMapFeature(feature: MapGeoJSONFeatureWithLayer): Entity | undefined {
        if (!feature.geometry || feature.geometry.type === 'GeometryCollection') {
            return undefined;
        }

        const entityId = feature.properties['uuid'];
        AssertService.isString(entityId);

        const businessIdentifierValue = feature.properties[this.identifyingProperty()];
        AssertService.isString(businessIdentifierValue);

        const depositId = feature.properties['deposit_id'];
        AssertService.isString(depositId);

        return {
            ...feature.properties,
            businessIdentifier: businessIdentifierValue,
            deposit_id: depositId,
            geom: feature.geometry,
            layer_id: feature.source,
            uuid: entityId,
        };
    }

    protected override getEntityId(entity: Entity): string {
        return entity.uuid;
    }

    protected override getLayerId(entity: Entity): string {
        AssertService.isString(entity['layer_id']);

        return entity['layer_id'];
    }

    private async createFieldInterventionEntities(dataEntities: DataEntity[], layerId: string): Promise<void> {
        await radashParallel(10, dataEntities, (dataEntity: DataEntity) => {
            const fieldInterventionEntityRequestParams: CreateFieldInterventionEntityDto = {
                dataAttributes: radashOmit(dataEntity, ['geom', 'deposit_id', 'uuid']),
                fieldIntervention: this.fieldInterventionId(),
                geom: dataEntity.geom!,
                layer: layerId,
                refSid: dataEntity.uuid,
            };

            this.updatedEntities.update((count: number): number => count + 1);

            return firstValueFrom(
                this.fieldInterventionEntitiesService.fieldInterventionEntityControllerPost({
                    createFieldInterventionEntityDto: fieldInterventionEntityRequestParams,
                }),
            );
        });
    }

    private getCurrentZoneDelimiterIfFilterActive(): string | undefined {
        return this.isZoneDelimiterFilterActive() ? this.zoneDelimiter() : undefined;
    }

    private mergeEntitiesByUuid(entities: Entity[], selectedEntities: Entity[]): Entity[] {
        return Array.from(
            new Map(
                [...entities, ...selectedEntities]
                    .filter((entity: Entity) => !!entity.uuid)
                    .map((entity: Entity) => [entity.uuid, entity]),
            ).values(),
        );
    }

    private openFetchEntitiesProgressModal(): MatDialogRef<FetchEntitiesProgressModalComponent> {
        const dialogRef = this.matDialog.open(FetchEntitiesProgressModalComponent, {
            autoFocus: false,
            minWidth: '40vw',
        });

        dialogRef.afterClosed().subscribe(async () => {});

        return dialogRef;
    }

    private async removeObsoleteFieldInterventionEntities(entities: FieldInterventionEntity[]): Promise<void> {
        await radashParallel(10, entities, (fieldInterventionEntity: FieldInterventionEntity) => {
            this.updatedEntities.update((count: number): number => count + 1);

            return firstValueFrom(
                this.fieldInterventionEntitiesService.fieldInterventionEntityControllerDelete({
                    id: fieldInterventionEntity.id,
                }),
            );
        });
    }

    private async updateFieldInterventionEntitiesForLayer(layerId: string): Promise<void> {
        const fieldInterventionEntitiesForCurrentLayer: FieldInterventionEntity[] =
            this.alreadySelectedFieldInterventionEntities.filter(
                (entity: FieldInterventionEntity): boolean => entity.layer === layerId && entity.refSid !== null,
            );
        const fieldInterventionDataEntitiesRefSids: string[] = fieldInterventionEntitiesForCurrentLayer.map(
            (entity: FieldInterventionEntity): string => entity.refSid!,
        );
        const selectedDataEntitiesIds: string[] = this.selectedEntities()[layerId].map(
            (dataEntity: DataEntity): string => dataEntity.uuid,
        );
        const added: DataEntity[] = this.selectedEntities()[layerId].filter(
            (dataEntityId: DataEntity): boolean => !fieldInterventionDataEntitiesRefSids.includes(dataEntityId.uuid),
        );
        const removed: FieldInterventionEntity[] = fieldInterventionEntitiesForCurrentLayer.filter(
            (entity: FieldInterventionEntity): boolean => !selectedDataEntitiesIds.includes(entity.refSid!),
        );

        await this.createFieldInterventionEntities(added, layerId);
        await this.removeObsoleteFieldInterventionEntities(removed);
    }

    private updateVisibleFieldInterventionAreaEntities(
        entities: Entity[],
        keepSelectedEntitiesVisible = false,
    ): { take: number; visibleEntities: Entity[] } {
        const selectedEntitiesToKeepVisible = keepSelectedEntitiesVisible
            ? this.activeLayers().flatMap((layer: Layer) => this.selectedEntities()[layer.id] ?? [])
            : [];

        const visibleEntities = this.mergeEntitiesByUuid(entities, selectedEntitiesToKeepVisible);
        const take = this.paginationParameters().take || defaultPaginationParameters.take;

        this.paginationParameters.set({
            ...this.paginationParameters(),
            page: 0,
            take,
            totalItems: visibleEntities.length,
        });

        return { take, visibleEntities };
    }
}
